/**
 * Cart utilities for conversion, validation, and calculations
 */
import {
  addToBackendCart,
  clearBackendCart,
  calculateShipping as calculateShippingAPI,
} from "../services/api/orders.js";
import { getFrameById, getSunglassesById, getAccessoryById, getLensById } from "../services/api/products.js";

/**
 * Converts Redux cart item to backend format
 * @param {Object} cartItem - Cart item from Redux store
 * @returns {Object} Cart item formatted for backend API
 */
export const convertToBackendCartItem = (cartItem) => {
  // Handle customized frames/glasses
  if (cartItem.isCustomized && cartItem.customization) {
    const customization = cartItem.customization;

    if (customization.lensType) {
      // Check if it's frame-only selection
      if (customization.lensType.id === "frame-only") {
        // Frame-only option
        return {
          frame: cartItem.id,
          quantity: cartItem.quantity || 1,
          prescription_type: "none",
          original_documentId: cartItem.id,
        };
      }

      // Validate that we have actual lens data, not just lens type
      if (!customization.lensType.lensData?.documentId) {
        console.error("Missing lens data for frames+lenses order:", customization.lensType);
        throw new Error("Invalid lens selection. Please customize your frames again.");
      }

      // For customized frames with lenses, use the frame+lens structure the backend expects
      const result = {
        frame: cartItem.id,
        lenses: customization.lensType.lensData.documentId,
        quantity: cartItem.quantity || 1,
        lens_name: customization.lensType.name,
        original_documentId: cartItem.id,
      };

      // Add lens color if selected
      if (customization.color) {
        result.lens_color = customization.color.name || customization.color;
      }

      // Add customer notes if provided
      if (customization.comments) {
        result.customer_notes = customization.comments;
      }

      // Handle prescription data
      if (customization.prescription.type === "manual") {
        const prescData = customization.prescription.data;
        result.od_sph = prescData.rightEye?.sph;
        result.od_cyl = prescData.rightEye?.cyl;
        result.od_axis = prescData.rightEye?.axis;
        result.os_sph = prescData.leftEye?.sph;
        result.os_cyl = prescData.leftEye?.cyl;
        result.os_axis = prescData.leftEye?.axis;
        result.pd = prescData.pd;
      } else if (customization.prescription.type === "diopter") {
        result.diopters = parseFloat(customization.prescription.data);
      } else if (customization.prescription.type === "email") {
        result.prescription_type = "email";
      } else if (customization.prescription.type === "upload") {
        // Send uploaded prescription file ID to backend
        result.prescription_file = customization.prescription.data.id;
      }

      return result;
    }
  }

  const isAccessory = cartItem.type === "accessories";
  const isSunglasses = cartItem.type === "sunglasses";

  if (isAccessory) {
    const result = {
      accessory: cartItem.id,
      quantity: cartItem.quantity || 1,
      original_documentId: cartItem.id,
    };

    return result;
  }

  if (isSunglasses) {
    const result = {
      sunglass: cartItem.id,
      quantity: cartItem.quantity || 1,
      original_documentId: cartItem.id,
    };

    return result;
  }

  // Handle only Frames
  const result = {
    frame: cartItem.id,
    quantity: cartItem.quantity || 1,
    prescription_type: "none",
    original_documentId: cartItem.id,
  };

  return result;
};

/**
 * Validates and refreshes cart item prices from the backend
 * @param {Array} cartItems - Array of cart items to validate
 * @returns {Object} Validation result with updated items and change status
 */
export const validateCartPrices = async (cartItems) => {
  try {
    const updatedItems = [];
    let hasChanges = false;

    for (const item of cartItems) {
      try {
        // Handle customized frame+lens items differently
        if (item.isCustomized && item.customization && item.customization.lensType) {
          // Fetch current frame price
          let framePrice = 0;

          try {
            const frameResponse = await getFrameById(item.id);

            if (frameResponse.ok) {
              const frameData = await frameResponse.json();
              const frameProduct = frameData.data || frameData;
              framePrice = parseFloat(frameProduct.price_with_tax || frameProduct.price) || 0;
            } else {
              console.warn(`Failed to fetch frame ${item.id}:`, frameResponse.status, frameResponse.statusText);
            }
          } catch (frameError) {
            console.warn(`Error fetching frame price:`, frameError);
          }

          // Fetch current lens price
          let lensPrice = 0;
          const lensId = item.customization.lensType.lensData?.documentId;

          // Skip lens price fetch if no lens ID (frame-only)
          if (!lensId || item.customization.lensType.id === "frame-only") {
            // Frame-only, no lens price
          } else {
            try {
              const lensResponse = await getLensById(lensId);

              if (lensResponse.ok) {
                const lensData = await lensResponse.json();
                const lensProduct = lensData.data || lensData;
                lensPrice = parseFloat(lensProduct.price_with_tax || lensProduct.price) || 0;
              } else {
                console.warn(`Failed to fetch lens ${lensId}:`, lensResponse.status, lensResponse.statusText);
              }
            } catch (lensError) {
              console.warn(`Error fetching lens price:`, lensError);
            }
          }

          // Calculate combined current price
          const currentCombinedPrice = framePrice + lensPrice;
          const storedFinalPrice = parseFloat(item.finalPrice) || 0;

          // Check if combined price has changed
          if (Math.abs(currentCombinedPrice - storedFinalPrice) > 0.01) {
            hasChanges = true;
            updatedItems.push({
              ...item,
              finalPrice: currentCombinedPrice,
              originalFinalPrice: storedFinalPrice,
              priceChanged: true,
            });
          } else {
            updatedItems.push(item);
          }
        } else {
          // Handle regular items
          let currentProduct = null;

          try {
            let response;
            if (item.type === "frames") {
              response = await getFrameById(item.id);
            } else if (item.type === "sunglasses") {
              response = await getSunglassesById(item.id);
            } else if (item.type === "accessories") {
              response = await getAccessoryById(item.id);
            } else if (item.type === "lenses") {
              response = await getLensById(item.id);
            } else {
              console.warn(`Unknown item type: ${item.type}`);
            }

            if (response && response.ok) {
              const data = await response.json();
              currentProduct = data.data || data;
            } else if (response) {
              console.warn(`Failed to fetch product ${item.id}:`, response.status, response.statusText);
            }
          } catch (error) {
            console.warn(`Error fetching product ${item.id}:`, error);
          }

          if (currentProduct) {
            const currentPrice = parseFloat(currentProduct.price_with_tax || currentProduct.price);
            const storedPrice = parseFloat(item.price);

            // Check if price has changed (with small tolerance for floating point)
            if (Math.abs(currentPrice - storedPrice) > 0.01) {
              hasChanges = true;
              updatedItems.push({
                ...item,
                price: currentPrice,
                originalPrice: storedPrice,
                priceChanged: true,
              });
            } else {
              updatedItems.push(item);
            }
          } else {
            // Keep original item if product fetch fails
            updatedItems.push(item);
          }
        }
      } catch (itemError) {
        console.warn(`Failed to validate price for item ${item.id}:`, itemError);
        // Keep original item if validation fails
        updatedItems.push(item);
      }
    }

    return {
      updatedItems,
      hasChanges,
      changedItems: updatedItems.filter((item) => item.priceChanged),
    };
  } catch (error) {
    console.error("Error validating cart prices:", error);
    return {
      updatedItems: cartItems,
      hasChanges: false,
      changedItems: [],
      error: error.message,
    };
  }
};

/**
 * Syncs entire cart to backend by clearing existing items and adding new ones
 * @param {Array} cartItems - Array of cart items to sync
 * @returns {Promise<Array>} Array of sync results for each item
 */
export const syncCartToBackend = async (cartItems) => {
  try {
    // Import getCartSessionToken to check if we have an existing session
    const { getCartSessionToken } = await import("../services/api/client.js");

    // Only clear if we have an existing session token (meaning there's a backend cart to clear)
    const existingSessionToken = getCartSessionToken();

    if (existingSessionToken) {
      // We have a session, so there's a backend cart to clear
      try {
        const clearResponse = await clearBackendCart();
        if (!clearResponse.ok) {
          if (clearResponse.status === 401) {
            // Backend cart no longer exists (e.g. deleted from admin) — discard stale session
            // and continue so the items are re-synced to a fresh cart
            const { setCartSessionToken } = await import("../services/api/client.js");
            setCartSessionToken(null);
          } else {
            throw new Error(`Failed to clear backend cart: ${clearResponse.status} ${clearResponse.statusText}`);
          }
        }
      } catch (clearError) {
        throw new Error(`Cannot proceed with sync without clearing cart first: ${clearError.message}`);
      }
    }

    // If no items to sync, return success
    if (!cartItems || cartItems.length === 0) {
      return [];
    }

    // Sync cart items sequentially to prevent race conditions and multiple cart creation
    const { setCartSessionToken } = await import("../services/api/client.js");
    const results = [];
    let sessionTokenExtracted = false;

    for (let index = 0; index < cartItems.length; index++) {
      const item = cartItems[index];
      try {
        const backendItem = convertToBackendCartItem(item);

        const response = await addToBackendCart(backendItem);
        const result = await response.json();

        if (!response.ok) {
          results.push({
            status: "rejected",
            value: { success: false, error: result.message || "Unknown error", item },
          });
          continue;
        }

        // Extract and store session token from response body immediately on first success
        if (!sessionTokenExtracted) {
          // Strapi wraps responses in { data: ... }, so check both paths
          const sessionToken = result.data?.sessionToken || result.sessionToken;
          if (sessionToken) {
            setCartSessionToken(sessionToken);
            sessionTokenExtracted = true;
          }
        }

        results.push({
          status: "fulfilled",
          value: { success: true, item: backendItem },
        });
      } catch (error) {
        results.push({
          status: "rejected",
          value: { success: false, error: error.message, item },
        });
      }
    }

    // Check if any items failed to sync
    const failures = results.filter((result) => result.status === "rejected" || !result.value?.success);
    if (failures.length > 0) {
      throw new Error(`Failed to sync ${failures.length} items to backend`);
    }

    return results;
  } catch (error) {
    console.error("Error syncing cart to backend:", error);
    throw error;
  }
};

/**
 * Calculates total cart value from cart items
 * @param {Array} cartItems - Array of cart items
 * @returns {number} Total cart value
 */
export const calculateCartTotal = (cartItems) => {
  return cartItems.reduce((total, item) => {
    const itemPrice = item.finalPrice || item.price || 0;
    const quantity = item.quantity || 1;

    return total + itemPrice * quantity;
  }, 0);
};

/**
 * Calculates shipping cost for cart items
 * @param {Array} cartItems - Array of cart items
 * @param {Object} fallbackValues - Fallback shipping configuration
 * @returns {Promise<Object>} Shipping calculation result
 */
export const calculateShipping = async (cartItems, fallbackValues = {}) => {
  const { FALLBACK_FREE_SHIPPING_THRESHOLD = 75.0, FALLBACK_SHIPPING_COST = 5.0 } = fallbackValues;

  const subtotal = calculateCartTotal(cartItems);

  try {
    const response = await calculateShippingAPI(cartItems, subtotal);

    if (response.ok) {
      const data = await response.json();
      return {
        shipping: data.shipping || 0,
        freeShippingThreshold: data.freeShippingThreshold || FALLBACK_FREE_SHIPPING_THRESHOLD,
        total: subtotal + (data.shipping || 0),
      };
    } else {
      throw new Error(`Shipping calculation failed: ${response.status}`);
    }
  } catch (error) {
    console.warn("Failed to calculate shipping, using fallback values:", error);

    // Fallback calculation
    const isEligibleForFreeShipping = subtotal >= FALLBACK_FREE_SHIPPING_THRESHOLD;
    const shippingCost = isEligibleForFreeShipping ? 0 : FALLBACK_SHIPPING_COST;

    return {
      shipping: shippingCost,
      freeShippingThreshold: FALLBACK_FREE_SHIPPING_THRESHOLD,
      total: subtotal + shippingCost,
      usingFallback: true,
    };
  }
};

/**
 * Calculates remaining amount needed for free shipping
 * @param {number} subtotal - Current cart subtotal
 * @param {number} freeShippingThreshold - Free shipping threshold amount
 * @returns {number} Amount needed for free shipping (0 if already eligible)
 */
export const calculateRemainingForFreeShipping = (subtotal, freeShippingThreshold) => {
  const remaining = freeShippingThreshold - subtotal;

  return remaining > 0 ? remaining : 0;
};

/**
 * Formats cart summary with totals and shipping
 * @param {Array} cartItems - Array of cart items
 * @param {Object} shippingInfo - Shipping calculation result
 * @returns {Object} Formatted cart summary
 */
export const formatCartSummary = (cartItems, shippingInfo) => {
  const subtotal = calculateCartTotal(cartItems);
  const shipping = shippingInfo.shipping || 0;
  const total = subtotal + shipping;

  return {
    subtotal: parseFloat(subtotal.toFixed(2)),
    shipping: parseFloat(shipping.toFixed(2)),
    total: parseFloat(total.toFixed(2)),
    itemCount: cartItems.length,
    isEligibleForFreeShipping: shipping === 0 && subtotal >= (shippingInfo.freeShippingThreshold || 0),
    remainingForFreeShipping: calculateRemainingForFreeShipping(subtotal, shippingInfo.freeShippingThreshold || 0),
  };
};
